import React, { useState } from "react"
import PropTypes from "prop-types"
import { DateTime } from "luxon"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

import { useDebouncedCallback } from "use-debounce"
import { concretiseDate } from "./filters"
import Select from "../../ui/Select"
import styled from "styled-components"

const Container = styled.div`
  width: 100%;
  select {
    width: 100%;
  }
  input {
  outline: none;
  background: rgba(128,128,128,0.05);
  border-style: solid;
  border-radius: 4px;
  border-width: 1px;
  border-color: #555;
  color: ${(props) => props.theme.colours.text.primary};
  }
`

const CustomRange = styled.div`
  display: flex;
  white-space: nowrap;
  padding: 0.25rem;
  label {
    padding-right: 0.5rem;
  }
`

/** Allows the user to select a time range. This is a single control that
 * effectively sets two filter values (from, to), either to a predefined,
 * dynamic time range as per `dateTimeProducer` in `filter.js`, or
 * a `custom` range where the user picks a start and end date/time.
 */
const DateTimeFilter = ({ filter, setFilter, active }) => {
  // Map `from` and `to` to a single value for select
  const from = filter.from
  const to = filter.to
  // Map from filter to classifier
  const classifier =
    !from && !to
      ? ""
      : from == "m1h" && to == "now"
        ? "past1h"
        : from == "m24h" && to == "now"
          ? "past24h"
          : from == "m7d" && to == "now"
            ? "past7d"
            : from == "m30d" && to == "now"
              ? "past30d"
              : from == "sd" && to == "ed"
                ? "thisday"
                : from == "sw" && to == "ew"
                  ? "thisweek"
                  : from == "sm" && to == "em"
                    ? "thismonth"
                    : from == "sy" && to == "ey"
                      ? "thisyear"
                      : from == "syd" && to == "eyd"
                        ? "yesterday"
                        : from == "sm3d" && to == "eyd"
                          ? "prev3days"
                          : from == "slm" && to == "sm"
                            ? "lastmonth"
                            : "custom"

  // Classifier selected, map to filter
  const classifierChanged = (e) => {
    const c = e.target.value
    if (c == "") {
      setFilter({ ...filter, from: null, to: null })
    } else if (c == "past1h") {
      setFilter({ ...filter, from: "m1h", to: "now" })
    } else if (c == "past24h") {
      setFilter({ ...filter, from: "m24h", to: "now" })
    } else if (c == "past7d") {
      setFilter({ ...filter, from: "m7d", to: "now" })
    } else if (c == "past30d") {
      setFilter({ ...filter, from: "m30d", to: "now" })
    } else if (c == "thisday") {
      setFilter({ ...filter, from: "sd", to: "ed" })
    } else if (c == "thisweek") {
      setFilter({ ...filter, from: "sw", to: "ew" })
    } else if (c == "thismonth") {
      setFilter({ ...filter, from: "sm", to: "em" })
    } else if (c == "thisyear") {
      setFilter({ ...filter, from: "sy", to: "ey" })
    } else if (c == "yesterday") {
      setFilter({ ...filter, from: "syd", to: "eyd" })
    } else if (c == "prev3days") {
      setFilter({ ...filter, from: "sm3d", to: "eyd" })
    } else if (c == "lastmonth") {
      setFilter({ ...filter, from: "slm", to: "sm" })
    } else if (c == "custom") {
      // Set initial date range - 1 week?
      const now = DateTime.local()
      // If we already have dates, we use those so user can just tweak
      // To do that, we have to first make them concrete, i.e. filter could say `sd` (start of day)
      const from = filter.from
        ? concretiseDate(filter.from)
        : now.minus({ days: 7 }).startOf("day").toISO()
      const to = filter.to
        ? concretiseDate(filter.to)
        : now.endOf("day").toISO()
      setFilter({ ...filter, from, to })
    }
  }

  // Track changes to custom date/time in local state - pre-populating
  // from URL parameters if we're in 'custom' mode already
  const [customFrom, setCustomFrom] = useState(null) // Date obj
  const [customTo, setCustomTo] = useState(null) // Date obj

  // Update parent filter from custom from/to in state
  const updateParentUsingCustomDebounced = useDebouncedCallback(() => {
    setFilter({
      ...filter,
      from: customFrom.toISOString(),
      to: customTo.toISOString(),
    })
  }, 1000)

  // Custom date changed, update filter
  const handleCustomChanged = (fromTo) => (date) => {
    if (fromTo === "from") {
      setCustomFrom(date)
      if (customTo == null) {
        setCustomTo(new Date(filter.to))
      }
    } else if (fromTo === "to") {
      setCustomTo(date)
      if (customFrom == null) {
        setCustomFrom(new Date(filter.from))
      }
    }
    updateParentUsingCustomDebounced()
  }

  return (
    <Container>
      <Select value={classifier} onChange={classifierChanged} active={active}>
        <option value="">All time</option>
        <optgroup label="Rolling">
          <option value="past1h">Past hour</option>
          <option value="past24h">Past 24 hours</option>
          <option value="past7d">Past 7 days</option>
          <option value="past30d">Past 30 days</option>
        </optgroup>
        <optgroup label="Fixed">
          <option value="thisday">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="prev3days">Previous 3 days</option>
          <option value="thisweek">This week</option>
          <option value="thismonth">This month</option>
          <option value="lastmonth">Last month</option>
          <option value="thisyear">This year</option>
        </optgroup>
        <option value="custom">Custom dates</option>
      </Select>
      {classifier == "custom" && (
        <div>
          <CustomRange>
            <label>Fr</label>
            <DatePicker
              dateFormat="y-MM-dd HH:mm:ss"
              showTimeSelect
              selected={customFrom || new Date(concretiseDate(filter.from))}
              onChange={handleCustomChanged("from")}
            />
          </CustomRange>
          <CustomRange>
            <label>To</label>
            <DatePicker
              dateFormat="y-MM-dd HH:mm:ss"
              showTimeSelect
              selected={customTo || new Date(concretiseDate(filter.to))}
              onChange={handleCustomChanged("to")}
            />
          </CustomRange>
        </div>
      )}
    </Container>
  )
}

DateTimeFilter.propTypes = {
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  active: PropTypes.bool,
}

export default DateTimeFilter
